import request, { type UserConfig } from '@/utils/request';

/* ==================== 类型定义 ==================== */

// ========== 通用类型 ==========
export interface EffectiveTimePeriodItem {
  /** 兼容旧数据，HH:mm 或 HH:mm:ss */
  end?: string;
  /** 兼容旧数据，HH:mm 或 HH:mm:ss */
  start?: string;
}

export interface ZoneDetailItem {
  cluster_id?: number;
  cluster_name?: string;
  zone_id?: string;
}

export interface DynamicWaveRuleGroup {
  /** required  conf: (min_len=1, max_len=50) */
  group_name?: string;
  /** 分组ID,创建时可选,更新时必填 */
  id?: number;
  /** conf: (gte=0, lte=1, default=1) */
  on_hold?: number;
  /** required  conf: (gte=0, lte=255) */
  priority?: number;
  /** ZoneDetailItem */
  zone_detail_list?: ZoneDetailItem[];
  /** required */
  zone_id_list?: string[];
}

export interface ProductionPeriodInfo {
  /** (D-365):-365, (D-2):-2, (D-1):-1, D:0, (D+365):365 */
  end_day_type?: number;
  /** 表示距离零点的秒数  conf: (lt=86400, gte=0) */
  end_time?: number;
  /** (D-365):-365, (D-2):-2, (D-1):-1, D:0, (D+365):365 */
  start_day_type?: number;
  /** 表示距离零点的秒数  conf: (lt=86400, gte=0) */
  start_time?: number;
}

export interface ProductionPeriodItem {
  /** 生产周期ID */
  production_period_id?: string;
  /** 生产周期名称 */
  production_period_name?: string;
  /** 生产周期时间详情 */
  production_period_time_detail?: ProductionPeriodInfo[];
  production_period_time_point_list?: number[];
  /** 生产周期时间类型 */
  production_period_time_type?: number;
}

export interface DynamicWaveChannelConfigItem {
  channel_filter_type?: number;
  channel_list?: string[];
  relationship_between_production_period?: number;
  production_period_list?: ProductionPeriodItem[];
}

export interface DynamicWaveChannelServiceCodeItem {
  channel_id?: string;
  channel_name?: string;
  service_code_list?: string[];
}

export interface ShopGroupIdItem {
  shop_group_id: number;
  shop_group_name: string;
}

export interface ShopIdItem {
  shop_id: number;
  shop_name: string;
}

// ========== 创建动态波次规则 ==========
export interface CreateDynamicWaveRuleRequest {
  /** 0: BAU, 1: Campaign */
  rule_type?: number;
  /** 0: Custom Zone Priority, 1: Smart Area Clustering */
  order_grouping_mode?: number;
  max_zone_count_per_sub_picking_task?: number;
  max_cluster_count_per_sub_picking_task?: number;
  max_sector_count_per_sub_picking_task?: number;
  max_park_count_per_sub_picking_task?: number;
  /** required  conf: (gte=0, lte=4) */
  aggregation_level_list?: number[];
  /** required */
  channel_id_list?: string[];
  channel_config_list?: DynamicWaveChannelConfigItem[];
  channel_service_code_config_list?: DynamicWaveChannelServiceCodeItem[];
  /** conf: (gte=0, lte=2, default=1) */
  cross_spx_store_flag?: number;
  /** conf: (gte=0, lte=1, default=1) */
  default_volume_flag?: number;
  /** EffectiveTimePeriodItem,required */
  effective_time_period?: EffectiveTimePeriodItem[];
  /** required */
  fulfillment_chain_id_list?: string[];
  /** DynamicWaveRuleGroup,required, 动态波次分组列表 */
  group_list?: DynamicWaveRuleGroup[];
  /** conf: (gte=0, lte=1, default=0) */
  mass_wave_mode?: number;
  order_qty_per_pre_sorting_grid?: number;
  max_pre_sorting_grid_qty?: number;
  /** conf: (gte=0, default=0) */
  max_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  max_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_width?: number;
  /** conf: (gte=0, lte=2, default=0) */
  merge_mode?: number;
  /** conf: (gte=0, default=0) */
  min_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  min_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_width?: number;
  /** conf: (gte=0, lte=1, default=0) */
  multi_picker_mode?: number;
  order_size_list?: number[];
  sssq_swop_flag_list?: number[];
  /** required */
  order_structure_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_filter_type?: number;
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_subject_type?: number;
  outer_consumable_subject_list?: string[];
  /** required */
  pcot_range_type_list?: number[];
  pre_allocate_zone_list?: string[];
  /** ProductionPeriodItem */
  production_period_time_list?: ProductionPeriodItem[];
  /** required  conf: (min_len=1, max_len=50) */
  rule_name?: string;
  shop_group_id_list?: string[];
  /** required */
  shop_id_list?: number[];
  /** required */
  min_item_qty?: number;
  max_item_qty?: number;
  min_sku_qty?: number;
  max_sku_qty?: number;
  /** conf: (gte=0, lte=2, default=2) */
  split_type?: number;
  /** conf: (max_len=32) */
  store_group_template_id?: string;
  urgent_flag_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  urgent_must_in?: number;
  /** conf: (gte=0, lte=1, default=0) */
  urgent_order_mode?: number;
  // SPWMS-73563: New unified fields
  remark?: string;
  sku_size_list?: number[];
  sku_size_filter_type?: number;
  sku_tag_id_list?: string[];
  sku_tag_id_filter_type?: number;
  sku_attribute_list?: number[];
  sku_attribute_filter_type?: number;
  /** 0:Only,1:Include (empty list means All),2:Exclude */
  category_filter_type?: number;
  /** L1 品类 ID 列表；Include 且空列表即 All（不限制品类） */
  category_id_list?: number[];
  holding_order_list?: number[];
  // SPWMS-73563: Shop filter
  shop_filter_type?: number;
  shop_group_filter_type?: number;
  shop_filter?: number;
}

export interface CreateDynamicWaveRuleResponse {
  data?: {
    rule_id?: string;
  };
  message?: string;
  retcode?: number;
}

// ========== 复制动态波次规则 ==========
export interface CopyDynamicWaveRuleRequest {
  /** required, 要复制的规则ID  conf: (min_len=1, max_len=32) */
  rule_id?: string;
  rule_type?: number;
}

export interface CopyDynamicWaveRuleResponse {
  data?: {
    /** 复制后生成的新规则ID */
    rule_id?: string;
  };
  message?: string;
  retcode?: number;
}

// ========== 编辑动态波次规则 ==========
export interface EditDynamicWaveRuleRequest {
  /** 0: BAU, 1: Campaign */
  rule_type?: number;
  /** 0: Custom Zone Priority, 1: Smart Area Clustering */
  order_grouping_mode?: number;
  max_zone_count_per_sub_picking_task?: number;
  max_cluster_count_per_sub_picking_task?: number;
  max_sector_count_per_sub_picking_task?: number;
  max_park_count_per_sub_picking_task?: number;
  /** required  conf: (gte=0, lte=4) */
  aggregation_level_list?: number[];
  /** required */
  channel_id_list?: string[];
  channel_config_list?: DynamicWaveChannelConfigItem[];
  channel_service_code_config_list?: DynamicWaveChannelServiceCodeItem[];
  /** conf: (gte=0, lte=2, default=1) */
  cross_spx_store_flag?: number;
  /** conf: (gte=0, lte=1, default=1) */
  default_volume_flag?: number;
  /** EffectiveTimePeriodItem,required */
  effective_time_period?: EffectiveTimePeriodItem[];
  /** required */
  fulfillment_chain_id_list?: string[];
  /** DynamicWaveRuleGroup,required, 动态波次分组列表 */
  group_list?: DynamicWaveRuleGroup[];
  /** conf: (gte=0, lte=1, default=0) */
  mass_wave_mode?: number;
  order_qty_per_pre_sorting_grid?: number;
  max_pre_sorting_grid_qty?: number;
  /** conf: (gte=0, default=0) */
  max_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  max_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_width?: number;
  /** conf: (gte=0, lte=2, default=0) */
  merge_mode?: number;
  /** conf: (gte=0, default=0) */
  min_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  min_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_width?: number;
  /** conf: (gte=0, lte=1, default=0) */
  multi_picker_mode?: number;
  order_size_list?: number[];
  sssq_swop_flag_list?: number[];
  /** required */
  order_structure_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_filter_type?: number;
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_subject_type?: number;
  outer_consumable_subject_list?: string[];
  /** required */
  pcot_range_type_list?: number[];
  pre_allocate_zone_list?: string[];
  /** ProductionPeriodItem */
  production_period_time_list?: ProductionPeriodItem[];
  /** required, 要修改的规则ID  conf: (min_len=1, max_len=32) */
  rule_id?: string;
  /** required  conf: (min_len=1, max_len=50) */
  rule_name?: string;
  shop_group_id_list?: string[];
  /** required */
  shop_id_list?: number[];
  min_sku_qty?: number;
  max_sku_qty?: number;
  min_item_qty?: number;
  max_item_qty?: number;
  /** conf: (gte=0, lte=2, default=2) */
  split_type?: number;
  /** conf: (max_len=32) */
  store_group_template_id?: string;
  urgent_flag_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  urgent_must_in?: number;
  /** conf: (gte=0, lte=1, default=0) */
  urgent_order_mode?: number;
  // SPWMS-73563: New unified fields
  remark?: string;
  sku_size_list?: number[];
  sku_size_filter_type?: number;
  sku_tag_id_list?: string[];
  sku_tag_id_filter_type?: number;
  sku_attribute_list?: number[];
  sku_attribute_filter_type?: number;
  /** 0:Only,1:Include (empty list means All),2:Exclude */
  category_filter_type?: number;
  /** L1 品类 ID 列表；Include 且空列表即 All（不限制品类） */
  category_id_list?: number[];
  holding_order_list?: number[];
  // SPWMS-73563: Shop filter
  shop_filter_type?: number;
  shop_group_filter_type?: number;
  shop_filter?: number;
  rule_status?: number;
}

export interface EditDynamicWaveRuleResponse {
  data?: {
    /** 修改后的规则ID */
    rule_id?: string;
  };
  message?: string;
  retcode?: number;
}

// ========== 删除动态波次规则 ==========
export interface DeleteDynamicWaveRuleRequest {
  /** required, 要删除的规则ID  conf: (min_len=1, max_len=32) */
  rule_id?: string;
}

export interface DeleteDynamicWaveRuleResponse {
  data?: {};
  message?: string;
  retcode?: number;
}

// ========== 获取动态波次规则详情 ==========
export interface GetDynamicWaveRuleDetailQuery {
  /** string,required, 要获取的规则ID  conf: (min_len=1, max_len=32) */
  rule_id?: string;
}

export interface DynamicWaveRuleDetail {
  rule_type?: number;
  order_grouping_mode?: number;
  max_zone_count_per_sub_picking_task?: number;
  max_cluster_count_per_sub_picking_task?: number;
  max_sector_count_per_sub_picking_task?: number;
  max_park_count_per_sub_picking_task?: number;
  /** required  conf: (gte=0, lte=4) */
  aggregation_level_list?: number[];
  /** required */
  channel_id_list?: string[];
  channel_config_list?: DynamicWaveChannelConfigItem[];
  channel_service_code_config_list?: DynamicWaveChannelServiceCodeItem[];
  /** conf: (gte=0, lte=2, default=1) */
  cross_spx_store_flag?: number;
  /** conf: (gte=0, lte=1, default=1) */
  default_volume_flag?: number;
  /** EffectiveTimePeriodItem,required */
  effective_time_period?: EffectiveTimePeriodItem[];
  /** required */
  fulfillment_chain_id_list?: string[];
  /** DynamicWaveRuleGroup,required, 动态波次分组列表 */
  group_list?: DynamicWaveRuleGroup[];
  min_item_qty?: number;
  max_item_qty?: number;
  /** conf: (gte=0, lte=1, default=0) */
  mass_wave_mode?: number;
  order_qty_per_pre_sorting_grid?: number;
  max_pre_sorting_grid_qty?: number;
  /** conf: (gte=0, default=0) */
  max_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  max_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  max_sub_task_width?: number;
  /** conf: (gte=0, lte=2, default=0) */
  merge_mode?: number;
  /** conf: (gte=0, default=0) */
  min_item_qty_per_picking_task?: number;
  /** required  conf: (gte=1) */
  min_per_pick_order_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_per_sub_task_item_quantity?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sku_qty_per_sub_picking_task?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_height?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_length?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_volume?: number;
  /** conf: (gte=0, default=0) */
  min_sub_task_width?: number;
  /** conf: (gte=0, lte=1, default=0) */
  multi_picker_mode?: number;
  order_size_list?: number[];
  sssq_swop_flag_list?: number[];
  /** required */
  order_structure_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_filter_type?: number;
  /** conf: (gte=0, lte=1, default=1) */
  outer_consumable_subject_type?: number;
  outer_consumable_subject_list?: string[];
  /** required */
  pcot_range_type_list?: number[];
  pre_allocate_zone_list?: string[];
  /** required  conf: (gte=0) */
  priority?: number;
  /** ProductionPeriodItem */
  production_period_time_list?: ProductionPeriodItem[];
  rule_id?: string;
  /** required  conf: (min_len=1, max_len=50) */
  rule_name?: string;
  shop_group_list?: ShopGroupIdItem[];
  /** required */
  shop_list?: ShopIdItem[];
  min_sku_qty?: number;
  max_sku_qty?: number;
  /** conf: (gte=0, lte=2, default=2) */
  split_type?: number;
  /** conf: (max_len=32) */
  store_group_template_id?: string;
  urgent_flag_list?: number[];
  /** conf: (gte=0, lte=1, default=1) */
  urgent_must_in?: number;
  /** conf: (gte=0, lte=1, default=0) */
  urgent_order_mode?: number;
  // SPWMS-73563: New unified fields
  remark?: string;
  sku_size_list?: number[];
  sku_size_filter_type?: number;
  sku_tag_id_list?: string[];
  sku_tag_id_filter_type?: number;
  sku_attribute_list?: number[];
  sku_attribute_filter_type?: number;
  /** 0:Only,1:Include (empty list means All),2:Exclude */
  category_filter_type?: number;
  /** L1 品类 ID 列表；Include 且空列表即 All（不限制品类） */
  category_id_list?: number[];
  holding_order_list?: number[];
  // SPWMS-73563: Shop filter
  shop_filter_type?: number;
  shop_group_filter_type?: number;
  shop_filter?: number;
}

export interface GetDynamicWaveRuleDetailResponse {
  data?: DynamicWaveRuleDetail;
  message?: string;
  retcode?: number;
}

// ========== 搜索动态波次规则列表 ==========
export interface SearchDynamicWaveRuleListQuery {
  /** string,all: 获取所有规则 */
  searchDynamicWaveRuleListKey?: string;
  /** string,Rule ID/Name 模糊搜索 */
  rule_id?: string;
  /** string[],3PL列表 */
  channel_id_list?: string[];
  /** number[],Shop ID列表 */
  shop_id_list?: number[];
  /** string[],Shop Group列表 */
  shop_group_id_list?: string[];
  /** number[] */
  dynamic_wave_rule_group_id_list?: number[];
  /** number[],规则类型列表，0: BAU, 1: Campaign */
  rule_type_list?: number[];
  /** integer,conf: (gt=0, default=1) */
  pageno?: number;
  /** integer,conf: (gt=0, default=20) */
  count?: number;
}

export interface SearchDynamicWaveRuleListItem {
  rule_type?: number;
  order_grouping_mode?: number;
  /** Order Picking Aggregation Level */
  aggregation_level_list?: number[];
  /** 3PL列表 */
  channel_id_list?: string[];
  /** 3PL名称列表 */
  channel_name_list?: string[];
  /** Create Time */
  ctime?: number;
  /** Effective Time Period */
  effective_time_period?: string;
  /** Fulfill Chain列表 */
  fulfillment_chain_id_list?: string[];
  /** Fulfill Chain名称列表 */
  fulfillment_chain_name_list?: string[];
  /** Total Item Qty Per Picking Task 最大值 */
  max_item_qty_per_picking_task?: number;
  /** Total Order Per Picking Task 最大值 */
  max_per_pick_order_quantity?: number;
  /** Total SKU Qty Per Picking Task 最大值 */
  max_sku_qty_per_picking_task?: number;
  /** Merge Mode, 0: No Need, 1: Only Multi Picker Task Need, 2: All Task Need */
  merge_mode?: number;
  /** Total Item Qty Per Picking Task 最小值 */
  min_item_qty_per_picking_task?: number;
  /** Total Order Per Picking Task 最小值 */
  min_per_pick_order_quantity?: number;
  /** Total SKU Qty Per Picking Task 最小值 */
  min_sku_qty_per_picking_task?: number;
  /** Modify Time */
  mtime?: number;
  /** Multi-Picker Mode, 0: No, 1: Yes */
  multi_picker_mode?: number;
  /** 操作人 */
  operator?: string;
  /** Order Size列表 */
  order_size_list?: number[];
  /** Order Structure列表 */
  order_structure_list?: number[];
  /** 优先级 */
  priority?: number;
  /** Production Period ID */
  production_period_id?: string;
  /** Production Period名称 */
  production_period_name?: string;
  /** 规则ID */
  rule_id?: string;
  /** 规则名称 */
  rule_name?: string;
  /** Shop Group ID列表 */
  shop_group_id_list?: string[];
  /** Shop Group名称列表 */
  shop_group_name_list?: string[];
  /** Shop ID列表 */
  shop_id_list?: number[];
  /** Shop名称列表 */
  shop_name_list?: string[];
  /** 状态 */
  rule_status?: number;
  /** Urgent Flag列表 */
  urgent_flag_list?: number[];
  /** Urgent Order Must In Picking Task, 0: No, 1: Yes */
  urgent_must_in?: number;
  /** Urgent Order Mode, 0: Efficiency First, 1: Speed First */
  urgent_order_mode?: number;
  // SPWMS-73563: Description
  remark?: string;
}

export interface SearchDynamicWaveRuleListResponse {
  data?: {
    current_effective_rule_type?: number;
    count?: number;
    list?: SearchDynamicWaveRuleListItem[];
    pageno?: number;
    total?: number;
  };
  message?: string;
  retcode?: number;
}

// ========== 搜索动态波次规则分组列表 ==========

export interface SearchDynamicWaveRuleGroupListItem {
  /** 分组名称 */
  group_name?: string;
  /** 分组ID */
  id?: number;
}

export interface SearchDynamicWaveRuleGroupListResponse {
  data?: {
    list?: SearchDynamicWaveRuleGroupListItem[];
  };
  message?: string;
  retcode?: number;
}

// ========== 获取动态波次规则信息 ==========

export interface GetDynamicWaveRuleInfoResponse {
  data?: {
    current_effective_rule_type?: number;
    /** EffectiveTimePeriodItem,有效时间段 */
    effective_time_period?: EffectiveTimePeriodItem[];
  };
  message?: string;
  retcode?: number;
}

// ========== 动态波次规则配置咨询 ==========
export interface ConsultDynamicWaveRuleRequest {
  /** 多轮会话ID，首次请求可不传 */
  session_id?: string;
  /** 用户咨询内容 */
  user_prompt?: string;
  /** 业务域 */
  business_domain?: string;
  /** 规则快照 */
  rule_snapshot?: string;
  /** 咨询的规则字段名 */
  rule_field?: string;
  /** 字段配置值 */
  rule_value?: string;
}

export interface ConsultDynamicWaveRuleResponse {
  code?: number;
  data?: {
    answer?: string;
    session_id?: string;
    task_id?: number;
    status?: number;
  };
  message?: string;
  retcode?: number;
}

export interface GetConsultResultRequest {
  session_id?: string;
  task_id?: number;
}

export interface GetConsultResultResponse {
  code?: number;
  data?: {
    answer?: string;
    status?: number;
    feedback_status?: number;
  };
  message?: string;
  retcode?: number;
}

export interface FeedbackConsultAnswerRequest {
  session_id?: string;
  task_id?: number;
  feedback_status?: number;
}

export interface FeedbackConsultAnswerResponse {
  code?: number;
  data?: {
    feedback_status?: number;
  };
  message?: string;
  retcode?: number;
}

export interface CancelConsultRequest {
  session_id?: string;
  task_id?: number;
}

export interface CancelConsultResponse {
  code?: number;
  data?: {
    status?: number;
  };
  message?: string;
  retcode?: number;
}

export interface ConsultDynamicWaveRuleFinalResult {
  answer: string;
  session_id: string;
  task_id?: number;
  status?: number;
  feedback_status?: number;
}

export const ConsultDialogueStatus = {
  Processing: 0,
  Success: 1,
  Blocked: 2,
  Failed: 3,
  Cancelled: 4,
} as const;

export const ConsultFeedbackStatus = {
  Unrated: 0,
  Helpful: 1,
  Unhelpful: 2,
} as const;

// ========== 设置动态波次规则信息 ==========
export interface SetDynamicWaveRuleInfoRequest {
  /** EffectiveTimePeriodItem,有效时间段 */
  effective_time_period?: EffectiveTimePeriodItem[];
  /** 规则优先级列表 */
  rule_list?: string[];
  /** 规则状态列表，顺序与 rule_list 保持一致 */
  rule_status_list?: number[];
  /** 当前生效规则类型，0: BAU, 1: Campaign */
  current_effective_rule_type?: number;
}

export interface SetDynamicWaveRuleInfoResponse {
  data?: {};
  message?: string;
  retcode?: number;
}

// ========== 设置动态波次规则开关 ==========
export interface SetDynamicWaveRuleSwitchRequest {
  /** required, 规则ID  conf: (min_len=1, max_len=32) */
  rule_id?: string;
  /** required, 开关状态 0:停用 1:启用  conf: (gte=0, lte=1) */
  switch_status?: number;
}

export interface SetDynamicWaveRuleSwitchResponse {
  data?: {};
  message?: string;
  retcode?: number;
}

// ========== 搜索动态波次规则日志 ==========
export interface SearchDynamicWaveRuleLogQuery {
  /** string,Rule ID查询条件 */
  rule_id?: string;
  /** integer,conf: (gt=0, default=1) */
  pageno?: number;
  /** integer,conf: (gt=0, default=20) */
  count?: number;
}

export interface DynamicWaveRuleLogContent {
  effective_rule_type_before?: number;
  effective_rule_type_after?: number;
  /** EffectiveTimePeriodItem,有效时间段(修改后) */
  effective_time_period_after?: EffectiveTimePeriodItem[];
  /** EffectiveTimePeriodItem,有效时间段(修改前) */
  effective_time_period_before?: EffectiveTimePeriodItem[];
  /** 优先级列表(修改后) */
  priority_list_after?: string[];
  /** 优先级列表(修改前) */
  priority_list_before?: string[];
  /** 规则详情 */
  rule_detail?: DynamicWaveRuleDetail;
}

export interface SearchDynamicWaveRuleLogItem {
  /** 创建时间 */
  ctime?: number;
  /** 事件类型 */
  event_type?: number;
  /** 日志ID */
  id?: number;
  /** 日志内容 */
  log_content?: DynamicWaveRuleLogContent;
  /** 修改时间 */
  mtime?: number;
  /** 操作人 */
  operator?: string;
  /** 规则ID */
  rule_id?: string;
  /** 仓库ID */
  whs_id?: string;
}

export interface SearchDynamicWaveRuleLogResponse {
  data?: {
    count?: number;
    list?: SearchDynamicWaveRuleLogItem[];
    pageno?: number;
    total?: number;
  };
  message?: string;
  retcode?: number;
}

/* ==================== API 接口定义 ==================== */

// ========== 基础权限：PC.RuleCenter.WaveRule.DynamicWaveRule ==========
// 可见页面内容+View按钮

/**
 * 搜索动态波次规则列表
 * @description 查询动态波次规则列表,支持模糊搜索和筛选
 */
export const searchDynamicWaveRuleList = (
  params: SearchDynamicWaveRuleListQuery
): Promise<SearchDynamicWaveRuleListResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule */
  return request.post(
    '/api/v2/apps/config/waverule/search_dynamic_wave_rule_list',
    params
  );
};

/**
 * 搜索动态波次规则分组列表
 * @description 获取所有动态波次规则分组信息
 */
export const searchDynamicWaveRuleGroupList =
  (): Promise<SearchDynamicWaveRuleGroupListResponse> => {
    /* PermissionKey: ALL */
    return request.get(
      '/api/v2/apps/config/waverule/search_dynamic_wave_rule_group_list'
    );
  };

/**
 * 获取动态波次规则详情
 * @description 根据规则ID获取动态波次规则的详细配置信息
 */
export const getDynamicWaveRuleDetail = (
  params: GetDynamicWaveRuleDetailQuery
): Promise<GetDynamicWaveRuleDetailResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule */
  return request.get(
    '/api/v2/apps/config/waverule/get_dynamic_wave_rule_detail',
    {
      params,
    }
  );
};

/**
 * 获取动态波次规则信息
 * @description 获取动态波次规则的全局配置信息,如有效时间段等
 */
export const getDynamicWaveRuleInfo =
  (): Promise<GetDynamicWaveRuleInfoResponse> => {
    /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule */
    return request.get(
      '/api/v2/apps/config/waverule/get_dynamic_wave_rule_info'
    );
  };

/**
 * 动态波次规则配置咨询
 * @description 获取当前规则字段的AI解释信息
 */
export const submitConsultDynamicWaveRule = (
  data: ConsultDynamicWaveRuleRequest,
  userConfig: UserConfig = {}
): Promise<ConsultDynamicWaveRuleResponse> => {
  /* PermissionKey: PC.AIAgent.Access */
  return request.post(
    '/api/v2/apps/configcopilot/consult/consult_dynamic_wave_rule',
    data,
    userConfig
  );
};

export const consultDynamicWaveRule = submitConsultDynamicWaveRule;

export const getConsultResult = (
  data: GetConsultResultRequest,
  userConfig: UserConfig = {}
): Promise<GetConsultResultResponse> => {
  /* PermissionKey: PC.AIAgent.Access */
  return request.post(
    '/api/v2/apps/configcopilot/consult/get_consult_result',
    data,
    userConfig
  );
};

export const cancelConsult = (
  data: CancelConsultRequest,
  userConfig: UserConfig = {}
): Promise<CancelConsultResponse> => {
  /* PermissionKey: PC.AIAgent.Access */
  return request.post(
    '/api/v2/apps/configcopilot/consult/cancel_consult',
    data,
    userConfig
  );
};

export const feedbackConsultAnswer = (
  data: FeedbackConsultAnswerRequest,
  userConfig: UserConfig = {}
): Promise<FeedbackConsultAnswerResponse> => {
  /* PermissionKey: PC.AIAgent.Access */
  return request.post(
    '/api/v2/apps/configcopilot/consult/feedback_consult_answer',
    data,
    userConfig
  );
};

const checkApiSuccess = (res: {
  retcode?: number;
  code?: number;
  message?: string;
}) => {
  if (
    (res?.retcode !== undefined && res.retcode !== 0) ||
    (res?.code !== undefined && res.code !== 0)
  ) {
    throw new Error(res?.message || 'Request failed');
  }
};

const waitWithAbort = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('', 'AbortError'));
      },
      { once: true }
    );
  });

const consultCancelPromiseKey = '__configCopilotCancelPromise';

export const consultDynamicWaveRuleWithPolling = async (
  data: ConsultDynamicWaveRuleRequest,
  userConfig: UserConfig = {}
): Promise<ConsultDynamicWaveRuleFinalResult> => {
  const signal = (userConfig as any)?.signal as AbortSignal | undefined;
  const submitRes = await submitConsultDynamicWaveRule(data, userConfig);
  checkApiSuccess(submitRes);

  const sessionId = submitRes?.data?.session_id || data.session_id || '';
  const taskId = submitRes?.data?.task_id;
  if (!sessionId || !taskId) {
    return {
      answer: submitRes?.data?.answer || '',
      session_id: sessionId,
      task_id: taskId,
      status: submitRes?.data?.status,
    };
  }

  let cancelled = false;
  const cancelCurrent = () => {
    if (cancelled) return;
    cancelled = true;
    const cancelPromise = cancelConsult({
      session_id: sessionId,
      task_id: taskId,
    }).then((res) => {
      checkApiSuccess(res);
      return res;
    });
    if (signal) {
      (signal as any)[consultCancelPromiseKey] = cancelPromise;
    }
  };
  signal?.addEventListener('abort', cancelCurrent, { once: true });

  try {
    for (;;) {
      await waitWithAbort(2000, signal);
      const resultRes = await getConsultResult(
        {
          session_id: sessionId,
          task_id: taskId,
        },
        userConfig
      );
      checkApiSuccess(resultRes);
      const status = resultRes?.data?.status;
      if (status === ConsultDialogueStatus.Processing) {
        continue;
      }
      return {
        answer: resultRes?.data?.answer || '',
        session_id: sessionId,
        task_id: taskId,
        status,
        feedback_status: resultRes?.data?.feedback_status,
      };
    }
  } finally {
    signal?.removeEventListener('abort', cancelCurrent);
  }
};

// ========== 查看权限：PC.RuleCenter.WaveRule.DynamicWaveRule.View ==========
// 可见ViewLog按钮

/**
 * 搜索动态波次规则日志
 * @description 查询动态波次规则的操作日志记录
 */
export const searchDynamicWaveRuleLog = (
  params: SearchDynamicWaveRuleLogQuery
): Promise<SearchDynamicWaveRuleLogResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.View */
  return request.get(
    '/api/v2/apps/config/waverule/search_dynamic_wave_rule_log',
    {
      params,
    }
  );
};

// ========== 编辑权限：PC.RuleCenter.WaveRule.DynamicWaveRule.Edit ==========
// 可见Create、Edit、Delete、Copy按钮

/**
 * 创建动态波次规则
 * @description 创建新的动态波次规则配置
 */
export const createDynamicWaveRule = (
  data: CreateDynamicWaveRuleRequest
): Promise<CreateDynamicWaveRuleResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/create_dynamic_wave_rule',
    data
  );
};

/**
 * 复制动态波次规则
 * @description 根据现有规则ID创建一个副本
 */
export const copyDynamicWaveRule = (
  data: CopyDynamicWaveRuleRequest
): Promise<CopyDynamicWaveRuleResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/copy_dynamic_wave_rule',
    data
  );
};

/**
 * 编辑动态波次规则
 * @description 修改现有动态波次规则的配置信息
 */
export const editDynamicWaveRule = (
  data: EditDynamicWaveRuleRequest
): Promise<EditDynamicWaveRuleResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/edit_dynamic_wave_rule',
    data
  );
};

/**
 * 删除动态波次规则
 * @description 删除指定的动态波次规则
 */
export const deleteDynamicWaveRule = (
  data: DeleteDynamicWaveRuleRequest
): Promise<DeleteDynamicWaveRuleResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/delete_dynamic_wave_rule',
    data
  );
};

/**
 * 设置动态波次规则信息
 * @description 设置动态波次规则的全局配置,如有效时间段和优先级列表
 */
export const setDynamicWaveRuleInfo = (
  data: SetDynamicWaveRuleInfoRequest
): Promise<SetDynamicWaveRuleInfoResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/set_dynamic_wave_rule_info',
    data
  );
};

/**
 * 设置动态波次规则开关
 * @description 启用或停用指定的动态波次规则
 */
export const setDynamicWaveRuleSwitch = (
  data: SetDynamicWaveRuleSwitchRequest
): Promise<SetDynamicWaveRuleSwitchResponse> => {
  /* PermissionKey: PC.RuleCenter.WaveRule.DynamicWaveRule.Edit */
  return request.post(
    '/api/v2/apps/config/waverule/set_dynamic_wave_rule_switch',
    data
  );
};
